import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import useSWR from "swr";
import { useForm, isEmail, isNotEmpty } from "@mantine/form";
import { Button, Drawer, Flex, Group, Modal, NumberInput, Pagination, Select, SimpleGrid, Stack, Tabs, Text, Textarea, TextInput } from "@mantine/core";
import { fetcher } from "../../../../services/fetcher";
import { SalesFollowups_Api, SalesQueries_Api } from "../../../../api/endpoints";
import DataTable from "../../../../common/components/DataTable/DataTable";
import useDebouncedSearchQuery from "../../../../common/hooks/useDebouncedSearchQuery/useDebouncedSearchQuery";
import LeadFollowups from "../../container/LeadFollowups";
import { errorNotifications, successNotifications } from "../../../../utils/handleNotifications";

const PAGE_SIZE = 25;

const QUERY_TYPE_OPTIONS = [
  { value: "PAYMENT_ISSUE", label: "Payment Issue" },
  { value: "DOCUMENT_UPLOAD", label: "Document Upload" },
  { value: "MEDICAL_FORM", label: "Medical Form" },
  { value: "FORM_FILLING", label: "Form Filling" },
  { value: "OTHER", label: "Other" },
];

const QUERY_STATUS_OPTIONS = [
  { value: "PENDING", label: "Pending" },
  { value: "CLOSED", label: "Closed" },
];

const EMPTY_FOLLOWUP = {
  person_name: "",
  phone: "",
  due_date: null,
  followup_type: "OUTBOUND_CALL",
  priority: "MEDIUM",
  trek: "",
  notes: "",
};

const EMPTY_QUERY = {
  status: "PENDING",
  customer_name: "",
  phone: "",
  email: "",
  query_type: "PAYMENT_ISSUE",
  query_type_other: "",
  remarks: "",
};

const labelOf = (value) =>
  value
    ? String(value)
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "-";

const valuesFrom = (query) => ({
  status: query?.status || "PENDING",
  customer_name: query?.customer_name || "",
  phone: String(query?.phone || "").replace(/\D/g, "").slice(0, 15),
  email: query?.email || "",
  query_type: query?.query_type || "PAYMENT_ISSUE",
  query_type_other: query?.query_type_other || "",
  remarks: query?.remarks || "",
});

const toPayload = (values) => {
  const payload = {
    status: values.status,
    customer_name: values.customer_name.trim(),
    phone: values.phone.trim(),
    email: values.email.trim(),
    query_type: values.query_type,
    remarks: values.remarks.trim(),
  };
  if (values.query_type === "OTHER") payload.query_type_other = values.query_type_other.trim();
  return payload;
};

const queryValidate = {
  customer_name: (value) => (String(value).trim() ? null : "Name is required"),
  phone: (value) => (/^\d{10,15}$/.test(value) ? null : "Enter a 10–15 digit phone number"),
  email: isEmail("Invalid email"),
  query_type: isNotEmpty("Select a query type"),
  query_type_other: (value, values) =>
    values.query_type === "OTHER" && !String(value).trim() ? "Specify the query type" : null,
  remarks: (value) => (String(value).trim() ? null : "Remarks are required"),
};

const QueryFields = ({ form }) => {
  const typeOptions = QUERY_TYPE_OPTIONS.some((item) => item.value === form.values.query_type)
    ? QUERY_TYPE_OPTIONS
    : [{ value: form.values.query_type, label: labelOf(form.values.query_type) }, ...QUERY_TYPE_OPTIONS];
  const statusOptions = QUERY_STATUS_OPTIONS.some((item) => item.value === form.values.status)
    ? QUERY_STATUS_OPTIONS
    : [{ value: form.values.status, label: labelOf(form.values.status) }, ...QUERY_STATUS_OPTIONS];

  return (
    <>
      <Group align="center" gap="sm" wrap="nowrap">
        <Text size="sm" c="dimmed" fw={500}>
          Status:
        </Text>
        <Select
          data={statusOptions}
          allowDeselect={false}
          style={{ flex: 1 }}
          {...form.getInputProps("status")}
        />
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        <TextInput
          label="Customer name"
          placeholder="Full name"
          required
          withAsterisk
          {...form.getInputProps("customer_name")}
        />
        <NumberInput
          label="Phone"
          placeholder="10-digit number"
          hideControls
          allowDecimal={false}
          allowNegative={false}
          clampBehavior="none"
          maxLength={15}
          {...form.getInputProps("phone")}
          onChange={(value) =>
            form.setFieldValue("phone", String(value ?? "").replace(/\D/g, "").slice(0, 15))
          }
        />
        <TextInput
          label="Email"
          placeholder="customer@email.com"
          {...form.getInputProps("email")}
        />
        <Select
          label="Query type"
          placeholder="Select type"
          data={typeOptions}
          required
          withAsterisk
          allowDeselect={false}
          {...form.getInputProps("query_type")}
        />
      </SimpleGrid>
      {form.values.query_type === "OTHER" ? (
        <TextInput
          label="Other query type"
          placeholder="Describe the query type"
          required
          withAsterisk
          {...form.getInputProps("query_type_other")}
        />
      ) : null}
      <Textarea
        label="Remarks"
        placeholder="Describe the query"
        minRows={3}
        {...form.getInputProps("remarks")}
      />
    </>
  );
};

const QueryFormModal = ({ opened, onClose, onCreated }) => {
  const [submitting, setSubmitting] = useState(false);
  const form = useForm({ initialValues: EMPTY_QUERY, validate: queryValidate });

  const close = () => {
    form.reset();
    onClose();
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await fetcher(SalesQueries_Api, "POST", toPayload(values));
      successNotifications("Success! Query created");
      close();
      await onCreated();
    } catch (error) {
      errorNotifications(error?.response?.data || {}, error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal opened={opened} onClose={close} title="New Query" size="lg" centered>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="sm">
          <QueryFields form={form} />
          <Group justify="flex-end">
            <Button variant="default" type="button" onClick={close} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>Create Query</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

const QueryDrawer = ({ opened, onClose, query, onUpdated }) => {
  const [tab, setTab] = useState("edit");
  const [submitting, setSubmitting] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [followupForm, setFollowupForm] = useState(EMPTY_FOLLOWUP);
  const form = useForm({ initialValues: EMPTY_QUERY, validate: queryValidate });
  const { setValues } = form;
  const queryId = query?.id;

  const followupsUrl = opened && tab === "followups" && queryId
    ? `${SalesFollowups_Api}?query_handler=${queryId}&page_size=50`
    : null;
  const { data: followupsData, isLoading: isFollowupsLoading, mutate: mutateFollowups } = useSWR(
    followupsUrl,
    fetcher,
    { revalidateOnFocus: false, shouldRetryOnError: false }
  );
  const followups = useMemo(() => {
    const rows = followupsData?.results || followupsData?.data || followupsData || [];
    return Array.isArray(rows) ? rows : [];
  }, [followupsData]);

  useEffect(() => {
    if (!opened || !queryId) return;
    setTab("edit");
    setValues(valuesFrom(query));
    setFollowupForm({
      ...EMPTY_FOLLOWUP,
      person_name: query?.customer_name || "",
      phone: String(query?.phone || ""),
    });
  }, [opened, queryId, query, setValues]);

  const handleSubmit = async (values) => {
    if (!queryId) return;
    const payload = {
      ...toPayload(values),
      query_type_other: values.query_type === "OTHER" ? values.query_type_other.trim() : null,
    };
    setSubmitting(true);
    try {
      const updated = await fetcher(`${SalesQueries_Api}${queryId}/`, "PATCH", payload);
      successNotifications("Success! Query updated");
      await onUpdated({ ...query, ...payload, ...(updated?.id ? updated : {}) });
      onClose();
    } catch (error) {
      errorNotifications(error?.response?.data || {}, error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFollowupSubmit = async (event) => {
    event.preventDefault();
    if (!queryId) return;
    setScheduling(true);
    try {
      await fetcher(SalesFollowups_Api, "POST", {
        query_handler: queryId,
        person_name: followupForm.person_name,
        phone: followupForm.phone,
        due_date: followupForm.due_date ? dayjs(followupForm.due_date).format("YYYY-MM-DD") : null,
        followup_type: followupForm.followup_type,
        priority: followupForm.priority,
        trek: followupForm.trek,
        notes: followupForm.notes,
      });
      setFollowupForm((prev) => ({ ...prev, due_date: null, notes: "" }));
      successNotifications("Follow-up scheduled");
      await mutateFollowups();
    } catch (error) {
      errorNotifications(error?.response?.data || {}, error);
    } finally {
      setScheduling(false);
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="45%"
      title={
        <Stack gap={0}>
          <Text size="xs" c="dimmed">{query?.query_no || "Query"}</Text>
          <Text fw={700} size="lg">{query?.customer_name || "Edit query"}</Text>
        </Stack>
      }
    >
      <Tabs value={tab} onChange={(value) => value && setTab(value)}>
        <Tabs.List>
          <Tabs.Tab value="edit">Edit</Tabs.Tab>
          <Tabs.Tab value="followups">Follow-ups</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="edit" pt="md">
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="sm">
              <QueryFields form={form} />
              <Group justify="flex-end">
                <Button variant="default" type="button" onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button type="submit" loading={submitting}>Save</Button>
              </Group>
            </Stack>
          </form>
        </Tabs.Panel>
        <Tabs.Panel value="followups" pt="md">
          <LeadFollowups
            followups={followups}
            isLoading={isFollowupsLoading}
            followupForm={followupForm}
            onFollowupFormChange={(updates) => setFollowupForm((prev) => ({ ...prev, ...updates }))}
            onSubmit={handleFollowupSubmit}
            isSubmitting={scheduling}
          />
        </Tabs.Panel>
      </Tabs>
    </Drawer>
  );
};

const crmQueriesUrl = (scope, isAdmin, params) => {
  const base =
    isAdmin && scope === "all" ? `${SalesQueries_Api}global/` : SalesQueries_Api;
  return `${base}?${params.toString()}`;
};

const Queries = ({ listScope = "owner", isAdminUser = false, scopeTabs = null }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [opened, setOpened] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [activeQuery, setActiveQuery] = useState(null);
  const { apiSearchQuery } = useDebouncedSearchQuery(search);

  const url = useMemo(() => {
    const params = new URLSearchParams({
      ordering: "-created_on",
      page: String(page),
      page_size: String(PAGE_SIZE),
    });
    if (apiSearchQuery) params.set("search", apiSearchQuery);
    return crmQueriesUrl(listScope, isAdminUser, params);
  }, [apiSearchQuery, isAdminUser, listScope, page]);

  const { data, isLoading, mutate } = useSWR(url, fetcher, { revalidateOnFocus: false });

  const rows = useMemo(
    () =>
      (Array.isArray(data?.results) ? data.results : []).map((item) => ({
        ...item,
        query_type_label: item.query_type_other || labelOf(item.query_type),
        remarks_display: item.remarks || "-",
        handled_by_label: item.handled_by_name || "-",
      })),
    [data]
  );

  const total = Number(data?.count) || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeFrom = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeTo = total ? Math.min(page * PAGE_SIZE, total) : 0;

  useEffect(() => {
    if (data && page > totalPages) setPage(totalPages);
  }, [data, page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [listScope]);

  const config = {
    searchPlaceholder: "Search by name, phone, email, or query no...",
    doubleClickRow: true,
    onRowClick: (row) => {
      setActiveQuery(row);
      setDrawerOpened(true);
    },
    fields: [
      { key: "query_no", label: "QUERY NO" },
      { key: "customer_name", label: "CUSTOMER" },
      { key: "phone", label: "PHONE" },
      { key: "email", label: "EMAIL" },
      { key: "query_type_label", label: "TYPE" },
      { key: "status", label: "STATUS", type: "status" },
      { key: "handled_by_label", label: "HANDLED BY" },
      { key: "created_on", label: "CREATED", type: "date" },
    ],
    selectionActions: [{ label: "+ New Query", handler: () => setOpened(true) }],
  };

  return (
    <Stack>
      <DataTable
        data={rows}
        config={config}
        loading={isLoading}
        searchValue={search}
        onSearch={(event) => {
          setSearch(event.target.value);
          setPage(1);
        }}
        toolbarLeftContent={scopeTabs}
      />
      <Flex justify="space-between" align="center" px={4}>
        <Text size="sm" fw={600} c="#6C94B4">
          {total ? `Showing ${rangeFrom}–${rangeTo} of ${total} queries` : "Showing 0 queries"}
        </Text>
        <Pagination total={totalPages} value={page} onChange={setPage} radius="sm" size="sm" siblings={1} />
      </Flex>
      <QueryFormModal
        opened={opened}
        onClose={() => setOpened(false)}
        onCreated={async () => {
          if (page === 1) await mutate();
          else setPage(1);
        }}
      />
      <QueryDrawer
        opened={drawerOpened}
        query={activeQuery}
        onClose={() => setDrawerOpened(false)}
        onUpdated={async (next) => {
          setActiveQuery(next);
          await mutate();
        }}
      />
    </Stack>
  );
};

export default Queries;
